/**
 * Safe storage helpers — cross-browser hardened.
 *
 * Wrap all access to localStorage / sessionStorage to survive:
 *  - Safari private mode (QuotaExceededError on write)
 *  - iOS Lockdown Mode
 *  - Firefox strict mode (storage access blocked)
 *  - Embedded webviews where storage is disabled
 *  - SSR-like environments where `window` is missing
 *
 * All helpers return `null` / `false` on failure instead of throwing.
 * A single console.warn is emitted per failing key per session to avoid noise.
 */

const warned = new Set<string>();

function warnOnce(scope: string, key: string, err: unknown) {
  const tag = `${scope}:${key}`;
  if (warned.has(tag)) return;
  warned.add(tag);
  // eslint-disable-next-line no-console
  console.warn(`[safe-storage] ${scope} failed for "${key}":`, err);
}

function getLocal(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function getSession(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

// ---------------- localStorage ----------------

export function safeGetLocalStorage(key: string): string | null {
  const s = getLocal();
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch (e) {
    warnOnce("getLocal", key, e);
    return null;
  }
}

export function safeSetLocalStorage(key: string, value: string): boolean {
  const s = getLocal();
  if (!s) return false;
  try {
    s.setItem(key, value);
    return true;
  } catch (e) {
    warnOnce("setLocal", key, e);
    return false;
  }
}

export function safeRemoveLocalStorage(key: string): boolean {
  const s = getLocal();
  if (!s) return false;
  try {
    s.removeItem(key);
    return true;
  } catch (e) {
    warnOnce("removeLocal", key, e);
    return false;
  }
}

// ---------------- sessionStorage ----------------

export function safeGetSessionStorage(key: string): string | null {
  const s = getSession();
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch (e) {
    warnOnce("getSession", key, e);
    return null;
  }
}

export function safeSetSessionStorage(key: string, value: string): boolean {
  const s = getSession();
  if (!s) return false;
  try {
    s.setItem(key, value);
    return true;
  } catch (e) {
    warnOnce("setSession", key, e);
    return false;
  }
}

export function safeRemoveSessionStorage(key: string): boolean {
  const s = getSession();
  if (!s) return false;
  try {
    s.removeItem(key);
    return true;
  } catch (e) {
    warnOnce("removeSession", key, e);
    return false;
  }
}

// ---------------- Active session guard (cross-module) ----------------
//
// Non-React modules (safe-cache-refresh) cannot consume React contexts.
// ExamSessionContext mirrors `examInProgress` into a window flag so any
// module can safely check before performing a hard reload.

const ACTIVE_FLAG = "__MBLEARN_EXAM_OR_PRACTICE_ACTIVE__";

// Route patterns that always count as "active session" even if the
// in-memory flag wasn't set (defensive — covers reloads mid-session).
const ACTIVE_ROUTE_PATTERNS = [
  /^\/quiz(\/|$)/,
  /^\/exam(\/|$)/,
  /^\/exam-simulation(\/|$)/,
  /^\/real-exam(\/|$)/,
  /^\/practice(\/|$)/,
  /^\/study(\/|$)/,
  /^\/review-bank(\/|$)/,
  /^\/onboarding-quiz(\/|$)/,
];

export function setActiveSessionFlag(active: boolean): void {
  try {
    (window as unknown as Record<string, unknown>)[ACTIVE_FLAG] = active;
  } catch {
    /* ignore */
  }
}

export function isExamOrPracticeActive(): boolean {
  try {
    const flag = Boolean(
      (window as unknown as Record<string, unknown>)[ACTIVE_FLAG]
    );
    if (flag) return true;
    const path = window.location?.pathname ?? "";
    return ACTIVE_ROUTE_PATTERNS.some((re) => re.test(path));
  } catch {
    return false;
  }
}
